// src/payment/utils/validationSchemas.js
import * as Yup from 'yup';

/** —————————————————————————————————————
 * Ortak sabitler & yardımcılar
 * ————————————————————————————————————— */
export const ACCOUNT_TYPES = [
    'SOLE_PROPRIETORSHIP',          // Şahıs
    'JOINT_STOCK_COMPANY',          // A.Ş.
    'LIMITED_COMPANY',    // Ltd. Şti.
    'COOPERATIVE',
    'GENERAL_PARTNERSHIP'
];

const phoneRegex = /^[0-9+\-\s()]+$/;
const cardNumberRegex = /^[0-9\s]{13,19}$/; // boşluklar dahil 13–19
const cvvRegex = /^\d{3,4}$/;
const monthRegex = /^(0[1-9]|1[0-2])$/;
const year2DigitRegex = /^\d{2}$/;
const taxNumberRegex = /^\d{10}$/;
const tcNumberRegex = /^\d{11}$/;

/** Opsiyonel: TCKN basit kontrol (yalnızca uzunluk + tüm rakamlar) */
export const isValidTCKN = (value) => tcNumberRegex.test(String(value || ''));

/** Para bilgileri */
export const buildPaymentFields = () =>
    Yup.object({
        amount: Yup.number()
            .typeError('Geçerli bir tutar giriniz')
            .min(1, 'Tutar en az 1 TL olmalıdır')
            .required('Tutar gereklidir'),
        cc_owner: Yup.string()
            .required('Kart üzerindeki isim gereklidir')
            .min(2, 'İsim en az 2 karakter olmalıdır'),
        card_number: Yup.string()
            .required('Kart numarası gereklidir')
            .matches(cardNumberRegex, 'Kart numarası 13–19 rakam olmalı (boşluk kabul edilir)'),
        expiry_month: Yup.string()
            .required('Ay gereklidir')
            .matches(monthRegex, 'Ay 01–12 arasında olmalıdır'),
        expiry_year: Yup.string()
            .required('Yıl gereklidir')
            .matches(year2DigitRegex, 'Yıl 2 haneli olmalıdır'),
        cvv: Yup.string()
            .required('CVV gereklidir')
            .matches(cvvRegex, 'CVV en az 3 haneli olmalıdır'),
        installment_count: Yup.string()
            .required('Taksit sayısı gereklidir')
    });

/** Müşteri bilgileri — temel (ad, e-posta, telefon) */
export const buildBasicCustomerValidation = () =>
    Yup.object({
        customer_name: Yup.string()
            .required('Ad Soyad gereklidir')
            .min(2, 'Ad Soyad en az 2 karakter olmalıdır'),
        customer_email: Yup.string()
            .email('Geçerli bir e-posta adresi giriniz')
            .required('E-posta adresi gereklidir'),
        customer_phone: Yup.string()
            .required('Telefon numarası gereklidir')
            .matches(phoneRegex, 'Geçerli bir telefon numarası giriniz')
    });

/** Müşteri bilgileri — detaylı (adres + vergi/TC + şirket) */
export const buildFullCustomerValidation = () =>
    Yup.object({
        accountType: Yup.string()
            .oneOf(ACCOUNT_TYPES, 'Geçerli bir cari/bayi tipi seçiniz')
            .required('Cari/Bayi tipi gereklidir'),

        companyName: Yup.string()
            .required('Şirket adı gereklidir')
            .min(2, 'Şirket adı en az 2 karakter olmalıdır'),

        /** TC vs Vergi no — accountType’a göre koşullu */
        tcNumber: Yup.string().when('accountType', {
            is: (v) => v === 'SOLE_PROPRIETORSHIP',
            then: (schema) =>
                schema
                    .required('T.C. kimlik numarası gereklidir')
                    .matches(tcNumberRegex, 'T.C. kimlik numarası 11 hane olmalıdır'),
            otherwise: (schema) => schema.strip() // bu alanı şemadan çıkar
        }),

        taxNumber: Yup.string().when('accountType', {
            is: (v) => v && v !== 'SOLE_PROPRIETORSHIP',
            then: (schema) =>
                schema
                    .required('Vergi numarası gereklidir')
                    .matches(taxNumberRegex, 'Vergi numarası 10 hane olmalıdır'),
            otherwise: (schema) => schema.strip()
        }),

        taxOffice: Yup.string()
            .required('Vergi dairesi gereklidir')
            .min(2, 'Vergi dairesi en az 2 karakter olmalıdır'),

        customer_address: Yup.string()
            .required('Adres gereklidir')
            .min(10, 'Adres en az 10 karakter olmalıdır'),

        customer_district: Yup.string()
            .required('İlçe gereklidir')
            .min(2, 'İlçe en az 2 karakter olmalıdır'),

        customer_province: Yup.string()
            .required('İl gereklidir')
            .min(2, 'İl en az 2 karakter olmalıdır'),

        /** Ülke zorunlu değil; formda readOnly geliyor */
        customer_country: Yup.string().nullable()
    });

/** Sadece e-posta istenen minimal durum (accountId varsa) */
export const buildEmailOnlyValidation = () =>
    Yup.object({
        customer_email: Yup.string()
            .email('Geçerli bir e-posta adresi giriniz')
            .required('E-posta adresi gereklidir')
    });

/**
 * ANA ŞEMA OLUŞTURUCU
 * flags:
 *  - showCustomerForm: boolean
 *  - showOnlyEmail: boolean  (showCustomerForm true iken)
 *  - requiresCustomerInfo: boolean (adres/TC/vergi vs. gereksinimi)
 *
 * Dönüş: Yup.ObjectSchema
 */
export const buildValidationSchema = (flags = {}) => {
    const { showCustomerForm, showOnlyEmail, requiresCustomerInfo } = flags;

    // Her durumda ödeme alanları
    let shape = {
        ...buildPaymentFields().fields
    };

    if (showCustomerForm) {
        if (showOnlyEmail) {
            shape = {
                ...shape,
                ...buildEmailOnlyValidation().fields
            };
        } else {
            // temel müşteri alanları
            shape = {
                ...shape,
                ...buildBasicCustomerValidation().fields
            };

            if (requiresCustomerInfo) {
                // detaylı müşteri alanları
                // Not: accountType üzerinden tcNumber/taxNumber koşulları çalışır
                shape = {
                    ...shape,
                    ...buildFullCustomerValidation().fields
                };
            }
        }
    }

    return Yup.object(shape);
};